import React, { useMemo } from 'react';

import { Avatar, Icon, Menu, Tooltip } from '@atom/mui';
import { LensIcon, PeopleIcon, PersonIcon } from '@atom/mui/icons';
import { getTaskUserStatusValues } from '@atom/selectors/taskSelectors';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { TaskUser } from '@atom/types/task';
import { UserDetail } from '@atom/types/user';
import { WorkOrderUserGroup } from '@atom/types/work';
import { getColorFromColorId } from '@atom/utilities/colorUtilities';
import { getUserFullName, sortByFullName } from '@atom/utilities/userUtilities';

import classes from './workUsers.module.css';

const { MenuItem } = Menu;

const styles = {
  condensed: {
    background: colors.neutral.white,
    color: colors.neutral.gray,
    fontSize: fonts.sm,
    border: `0.5px solid ${colors.neutral.silver}`,
  },
  statusIcon: {
    position: 'absolute',
    top: '24px',
    left: '21px',
    fontSize: '10px',
    padding: '0',
  },
};

interface Props {
  users?: UserDetail[];
  userGroups?: WorkOrderUserGroup[];
  avatarSize?: number;
  showTaskUserStatus?: boolean;
  useNoAssigneesIcon?: boolean;
  alignRight?: boolean;
}

const isUser = (user: UserDetail | WorkOrderUserGroup): user is UserDetail => {
  return user.hasOwnProperty('firstName');
};

const getAvatarStyle = (size: number, alignRight: boolean) => ({
  height: size,
  width: size,
  marginRight: alignRight ? 0 : '5px',
});

const getUserAvatar = (
  user: UserDetail | TaskUser,
  size: number,
  showTaskUserStatus: boolean,
  tooltip: boolean,
  alignRight?: boolean,
): React.ReactNode => (
  <div className={classes.thumbnailContainer}>
    <Tooltip title={tooltip ? getUserFullName(user) : ''} placement="bottom">
      <span>
        <Avatar
          style={getAvatarStyle(size, alignRight)}
          src={user.photoUrl}
          alt={user.firstName}
        />
        {showTaskUserStatus && (
          <>
            <div className={classes.statusBackground} />
            <Icon
              style={styles.statusIcon}
              color={getTaskUserStatusValues(user.status).color}
              icon={LensIcon}
            />
          </>
        )}
      </span>
    </Tooltip>
  </div>
);

const getUserGroupAvatar = (
  group: WorkOrderUserGroup,
  size: number,
  tooltip = true,
  alignRight?: boolean,
): React.ReactNode => (
  <Tooltip title={tooltip ? group.name : ''} placement="bottom">
    <span>
      <Avatar
        style={{
          ...getAvatarStyle(size, alignRight),
          background: getColorFromColorId(group.colorId || 0),
        }}
        alt={group.name}
      >
        <Icon
          style={{ fontSize: 20 }}
          color={colors.neutral.white}
          icon={PeopleIcon}
        />
      </Avatar>
    </span>
  </Tooltip>
);

const WorkUsers = ({
  users = [],
  userGroups = [],
  avatarSize = 26,
  showTaskUserStatus = false,
  useNoAssigneesIcon,
  alignRight,
}: Props) => {
  // remove dupes
  const usersMap = [...users].reduce(
    (acc: { [id: string]: UserDetail }, user) => ({ ...acc, [user.id]: user }),
    {},
  );

  const sortedUsers = useMemo(() => {
    return Object.values(usersMap).sort(sortByFullName);
  }, [users]);

  const sortedUserGroups = useMemo(() => {
    // remove dupes
    const userGroupMap = [...userGroups].reduce(
      (acc: { [id: string]: WorkOrderUserGroup }, userGroup) => {
        return { ...acc, [userGroup.id]: userGroup };
      },
      {},
    );

    return Object.values(userGroupMap).sort((first, second) =>
      first.name.localeCompare(second.name),
    );
  }, [userGroups]);

  const usersAndGroups = [...sortedUserGroups, ...sortedUsers];
  const initial = usersAndGroups.slice(0, 2);
  const condensed = usersAndGroups.slice(2);

  return initial.length ? (
    <div className={classes.container}>
      {initial.map((userOrGroup, index) => {
        return (
          <React.Fragment key={userOrGroup.id + index}>
            {isUser(userOrGroup)
              ? getUserAvatar(userOrGroup, avatarSize, showTaskUserStatus, true)
              : getUserGroupAvatar(userOrGroup, avatarSize)}
          </React.Fragment>
        );
      })}
      {!!condensed.length && (
        <Menu
          noIconButton
          variant="menu"
          disableAutoFocusItem
          icon={
            <Avatar
              style={{
                ...getAvatarStyle(avatarSize, alignRight),
                ...styles.condensed,
              }}
            >
              {condensed.length}
            </Avatar>
          }
        >
          {condensed.map(userOrGroup => {
            const name = isUser(userOrGroup)
              ? getUserFullName(userOrGroup)
              : userOrGroup.name;

            return (
              <MenuItem
                key={userOrGroup.id}
                startAdornment={
                  isUser(userOrGroup)
                    ? getUserAvatar(
                        userOrGroup,
                        avatarSize,
                        showTaskUserStatus,
                        false,
                      )
                    : getUserGroupAvatar(userOrGroup, avatarSize, false)
                }
              >
                {name}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </div>
  ) : useNoAssigneesIcon ? (
    <Tooltip title="Unassigned" placement="bottom">
      <span>
        <Avatar
          style={{
            ...getAvatarStyle(avatarSize, alignRight),
            background: colors.neutral.gray,
          }}
          alt="Unassigned"
        >
          <Icon
            style={{ fontSize: avatarSize }}
            color={colors.neutral.white}
            icon={PersonIcon}
          />
        </Avatar>
      </span>
    </Tooltip>
  ) : (
    <div>No Assignees</div>
  );
};

export default WorkUsers;
